/*
 * Copyright (C) Fraunhofer IESE 2021-2024 - Pedram (Majid) Jokar, Mher Ter-Tovmasyan,
 * Bestin John, Steffen Hupp, Anna Kleiner, Rafael Aranda Lopez King, Emily Calvet, Philipp Ewen,
 * Matthias Gerbershagen, Milad Chatrangoon, Stefan Schweitzer
 *
 * SPDX-License-Identifier: AGPL-3.0-or-later
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import { SLRSideBar, SLRSideBarItem, useWindowSize } from '@slr/shared-library';
import {
  MOBILE_VIEW_WIDTH,
  PROFILE_ORGANIZATIONS_PATH,
  PROFILE_PATH
} from 'configs';
import { useGetCurrentUserProfileWithSolution1 } from 'hooks';
import { FC, PropsWithChildren, useMemo } from 'react';
import { Container } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { AddressForm } from './address/AddressForm';
import { ProfileDetails } from './details/ProfileDetails';
import { getProfileNavItems } from './profileNavItems';

export const ProfilePage: FC<PropsWithChildren> = ({ children }) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'profilePage'
  });

  const { isMobileView } = useWindowSize(MOBILE_VIEW_WIDTH);
  const { pathname } = useLocation();
  const { data: currentUserProfile } = useGetCurrentUserProfileWithSolution1();

  const isMainProfileRoute = pathname === PROFILE_PATH;

  const navItems = useMemo(
    () => getProfileNavItems(t, currentUserProfile),
    [t, currentUserProfile]
  );

  // Normalize current path for sidebar highlighting - treat /profile/organizations/* as /profile/organizations
  const currentPath = useMemo(() => {
    if (pathname.startsWith(PROFILE_ORGANIZATIONS_PATH)) {
      return PROFILE_ORGANIZATIONS_PATH;
    }
    return pathname;
  }, [pathname]);

  return (
    <div className="profile-page">
      {isMobileView ? (
        <div className="p-4">
          {isMainProfileRoute ? (
            <>
              <ProfileDetails />
              <AddressForm />
            </>
          ) : (
            children
          )}
        </div>
      ) : (
        <Container className="py-6">
          <SLRSideBar
            items={navItems.map((item) => (
              <SLRSideBarItem key={item.key} to={item.to} label={item.label} />
            ))}
            title={t('title')}
            currentPath={currentPath}
          >
            {isMainProfileRoute ? (
              <>
                <ProfileDetails />
                <AddressForm />
              </>
            ) : (
              children
            )}
          </SLRSideBar>
        </Container>
      )}
    </div>
  );
};
